import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertCircle, RefreshCw, Copy, ChevronDown, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/admin/sync-errors")({
  head: () => ({
    meta: [
      { title: "Sync-fejl — AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminOnlyGuard>
      <AdminSyncErrors />
    </AdminOnlyGuard>
  ),
});

type SyncErrorRow = {
  id: string;
  status: string;
  source: string;
  duration_ms: number | null;
  triggered_by: string;
  created_at: string;
  error_message: string | null;
  metadata: {
    failedStep?: string;
    stack?: string | null;
    recentCarIds?: string[];
    itemErrors?: Array<{ bilinfoId: string; op: string; message: string; attempts?: number }>;
    itemErrorCount?: number;
    retryAttemptCount?: number;
    retrySuccessCount?: number;
    mode?: string;
  } | null;
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("da-DK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} kopieret`))
    .catch(() => toast.error("Kunne ikke kopiere"));
}

function ErrorRow({ row }: { row: SyncErrorRow }) {
  const [open, setOpen] = useState(false);
  const md = row.metadata ?? {};
  const stack = md.stack ?? null;
  const failedStep = md.failedStep ?? "ukendt";
  const itemErrors = md.itemErrors ?? [];

  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Badge variant="destructive" className="gap-1">
                <AlertCircle size={12} />
                Fejl
              </Badge>
              <Badge variant="outline" className="font-mono">
                step: {failedStep}
              </Badge>
              {md.mode && <Badge variant="secondary">{md.mode}</Badge>}
              <Badge variant="outline">{row.triggered_by}</Badge>
              {row.duration_ms != null && (
                <span className="text-xs text-muted-foreground">
                  {(row.duration_ms / 1000).toFixed(1)}s
                </span>
              )}
            </div>
            <CardTitle className="text-base font-mono break-words">
              {row.error_message ?? "(uden besked)"}
            </CardTitle>
            <CardDescription>{formatDate(row.created_at)}</CardDescription>
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(row.error_message ?? "", "Fejlbesked");
              }}
            >
              <Copy size={14} className="mr-1" /> Kopiér besked
            </Button>
            {stack && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(stack, "Stacktrace");
                }}
              >
                <Copy size={14} className="mr-1" /> Kopiér stack
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(JSON.stringify(row, null, 2), "Hele kørslen");
              }}
            >
              <Copy size={14} className="mr-1" /> Kopiér JSON
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Failed step</div>
              <div className="font-mono">{failedStep}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Item-fejl</div>
              <div className="font-mono">
                {md.itemErrorCount ?? itemErrors.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Retries</div>
              <div className="font-mono">
                {md.retrySuccessCount ?? 0} / {md.retryAttemptCount ?? 0}
              </div>
            </div>
          </div>

          {stack ? (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Stacktrace (server)
              </div>
              <pre className="text-xs bg-muted/40 border rounded p-3 overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
                {stack}
              </pre>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Ingen stacktrace gemt for denne kørsel.
            </p>
          )}

          {itemErrors.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Item-fejl (seneste {itemErrors.length})
              </div>
              <div className="space-y-1">
                {itemErrors.map((it, i) => (
                  <div
                    key={i}
                    className="text-xs font-mono border rounded p-2 bg-muted/30"
                  >
                    <span className="text-foreground">{it.op}</span>{" "}
                    <span className="text-muted-foreground">#{it.bilinfoId}</span>
                    {it.attempts != null && (
                      <span className="text-muted-foreground"> · {it.attempts} forsøg</span>
                    )}
                    <div className="text-destructive break-words">{it.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 border-t">
            <Link
              to="/admin/sync"
              className="text-xs text-primary hover:underline"
            >
              → Åbn Bilinfo-sync
            </Link>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function AdminSyncErrors() {
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState(25);

  const { data: rows, isLoading, isFetching } = useQuery({
    queryKey: ["sync-error-logs", limit],
    queryFn: async (): Promise<SyncErrorRow[]> => {
      const { data, error } = await supabase
        .from("sync_logs")
        .select(
          "id,status,source,duration_ms,triggered_by,created_at,error_message,metadata",
        )
        .eq("status", "error")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as SyncErrorRow[];
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertCircle className="text-destructive" /> Sync-fejl
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Seneste fejlede Bilinfo-sync-kørsler med <span className="font-mono">failedStep</span> og
            serverstack til debugging.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["sync-error-logs"] })}
          disabled={isFetching}
        >
          <RefreshCw size={14} className={`mr-1 ${isFetching ? "animate-spin" : ""}`} />
          Genindlæs
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Indlæser…</p>
      ) : !rows || rows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Ingen fejlede sync-kørsler fundet.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {rows.map((row) => (
              <ErrorRow key={row.id} row={row} />
            ))}
          </div>
          {rows.length >= limit && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setLimit((l) => l + 25)}>
                Vis flere
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
